"use client";

import { Heading, Icon, ImageWrapper, Link } from "@farm-junction/design-library";
import type { ReactNode, Ref } from "react";

const headerLinkClass =
    "!flex !w-full !items-center !justify-between !gap-2 !font-normal !text-inherit hover:!text-inherit";

export type PdpModelSummaryCardProps = {
    brandName: string;
    modelName: string;
    href: string;
    productImageSrc: string;
    leftLabel: string;
    leftValue: string;
    rightLabel: string;
    rightValue: string;
    rating?: number;
    footer?: ReactNode;
    className?: string;
    cardRef?: Ref<HTMLDivElement>;
    headerLinkRef?: Ref<HTMLAnchorElement>;
    footerRef?: Ref<HTMLDivElement>;
};

export default function PdpModelSummaryCard({
    brandName,
    modelName,
    href,
    productImageSrc,
    leftLabel,
    leftValue,
    rightLabel,
    rightValue,
    rating = 0,
    footer,
    className = "",
    cardRef,
    headerLinkRef,
    footerRef,
}: PdpModelSummaryCardProps) {
    return (
        <div ref={cardRef} className={`rounded-2xl border border-[#D8DEE8] ${className}`.trim()}>
            <Link
                ref={headerLinkRef}
                href={href}
                title={`${brandName} ${modelName}`}
                className={`${headerLinkClass} overflow-hidden rounded-t-2xl border-b border-[#E8ECF0] bg-[#F9F9F9] pl-4 !no-underline`}
            >
                <div className="min-w-0 pt-0.5">
                    <span className="mb-1 block text-[10px] font-medium uppercase leading-[14px] tracking-[0.06em] text-[#8B93A0]">
                        {brandName}
                    </span>
                    <Heading
                        variant="h2"
                        level={2}
                        color="black"
                        fontWeight="bold"
                        className="!mb-1 !text-[18px] !leading-[22px] !tracking-normal !text-[#101828]"
                    >
                        {modelName}
                    </Heading>
                    {rating > 0 && (
                        <div className="flex items-center gap-1">
                            <Icon name="star" width={10} height={10} className="h-2.5 w-2.5" />
                            <span className="text-[12px] font-semibold leading-4 text-[#101828]">
                                {rating.toFixed(1)}
                            </span>
                        </div>
                    )}
                </div>
                <ImageWrapper
                    src={productImageSrc}
                    alt={`${brandName} ${modelName}`}
                    title={`${brandName} ${modelName}`}
                    width={140}
                    height={82}
                    decoding="async"
                    className="h-[82px] w-[140px] shrink-0 rounded-tr-2xl object-cover"
                    fallbackSrc="/images/placeholder-car.svg"
                />
            </Link>

            <div className="grid grid-cols-[1.2fr_0.8fr] gap-3 p-4">
                <div>
                    <p className="text-[11px] leading-[14px] text-[#8B93A0]">{leftLabel}</p>
                    <p className="mt-1 text-[18px] font-bold leading-5 text-[#101828]">{leftValue}</p>
                </div>
                <div className="border-l border-[#E8ECF0] pl-3 text-right">
                    <p className="text-[11px] leading-[14px] text-[#8B93A0]">{rightLabel}</p>
                    <p className="mt-1 text-[13px] font-medium leading-5 text-[#101828]">{rightValue}</p>
                </div>
                {footer ? (
                    <div className="col-span-2" ref={footerRef}>
                        {footer}
                    </div>
                ) : ''}
            </div>
        </div>
    );
}
